import { styled } from '@linaria/react';
import dayjs from 'dayjs';
import type { RoadmapGoal } from '../../../../api/strategy-client/strategy-client.type';
import { Icon } from '../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { calculateGridlinePositions, calculateQuarterlinePositions } from '../roadmap.helpers';
import type { DeliveryProgressFilter } from '../roadmap.type';
import { GOAL_HEADER_HEIGHT, LEFT_PADDING } from '../roadmap.type';
import { OutcomeSection } from './outcome-section';

interface GoalSectionProps {
  goal: RoadmapGoal;
  isExpanded: boolean;
  onToggle: () => void;
  expandedOutcomes: Set<string>;
  onToggleOutcome: (outcomeId: string) => void;
  timelineStart: dayjs.Dayjs;
  timelineEnd: dayjs.Dayjs;
  timelineWidth: string | number;
  totalDays: number;
  deliveryProgressFilter: DeliveryProgressFilter;
  selectedYear: number | null;
}

export function GoalSection({
  goal,
  isExpanded,
  onToggle,
  expandedOutcomes,
  onToggleOutcome,
  timelineStart,
  timelineEnd,
  timelineWidth,
  totalDays,
  deliveryProgressFilter,
  selectedYear,
}: GoalSectionProps) {
  const gridlines = selectedYear
    ? calculateGridlinePositions(timelineStart, timelineEnd, totalDays)
    : calculateQuarterlinePositions(timelineStart, timelineEnd, totalDays);
  const hasOutcomes = goal.outcomes.length > 0;

  return (
    <GoalSectionStyled>
      <GoalHeader onClick={() => hasOutcomes && onToggle()}>
        <GoalLabel>
          {hasOutcomes && (
            <ExpandIcon>
              <Icon
                name={isExpanded ? 'expand_more' : 'chevron_right'}
                size={24}
                color="var(--base-white)"
                style={{ cursor: 'pointer' }}
              />
            </ExpandIcon>
          )}
          <Tooltip variant="black" label={goal.name} multiline maw={320}>
            <GoalName>{goal.name}</GoalName>
          </Tooltip>
        </GoalLabel>
        <GoalTimeline style={{ width: timelineWidth }}>
          {gridlines.map((gridline) => (
            <GridLine key={`goal-${gridline.label}`} style={{ left: `${gridline.leftPercent}%` }} />
          ))}
        </GoalTimeline>
      </GoalHeader>
      {isExpanded && hasOutcomes && (
        <OutcomesContainer>
          {goal.outcomes.map((outcome) => (
            <OutcomeSection
              key={outcome.id}
              outcome={outcome}
              isExpanded={expandedOutcomes.has(outcome.id)}
              onToggle={() => onToggleOutcome(outcome.id)}
              timelineStart={timelineStart}
              timelineEnd={timelineEnd}
              timelineWidth={timelineWidth}
              totalDays={totalDays}
              deliveryProgressFilter={deliveryProgressFilter}
              selectedYear={selectedYear}
            />
          ))}
        </OutcomesContainer>
      )}
    </GoalSectionStyled>
  );
}

// Styled components
const GoalSectionStyled = styled.div`
  border-bottom: 1px solid var(--sky-light);
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const GoalHeader = styled.div`
  height: ${GOAL_HEADER_HEIGHT}px;
  background: linear-gradient(135deg, #5e72e4 0%, #4c63d2 100%);
  color: #ffffff;
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  width: 100%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, #4c63d2 0%, #3a4fc0 100%);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  }
`;

const GoalLabel = styled.div`
  width: ${LEFT_PADDING}px;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  flex-shrink: 0;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

const GoalName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
`;

const ExpandIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const GoalTimeline = styled.div`
  border-left: 1px solid rgba(255, 255, 255, 0.15);
  border-right: 1px solid rgba(255, 255, 255, 0.15);
  flex: 1;
  position: relative;
  overflow: visible;
`;

const GridLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(255, 255, 255, 0.2);
  pointer-events: none;
`;

const OutcomesContainer = styled.div`
  background: var(--base-white);
  display: flex;
  flex-direction: column;
`;
